import { isDefined, resolveSchemaWithRef } from '@kaoto/forms';

import { ExpressionService } from '../../../../../components/Visualization/Canvas/Form/fields/ExpressionField/expression.service';
import { KaotoSchemaDefinition } from '../../../../kaoto-schema';

interface IValidationResult {
  level: 'error' | 'warning' | 'info';
  type: 'missingRequired';
  parentPath: string;
  propertyName: string;
  message: string;
}

function isEmptyOrNotArray(value: unknown): boolean {
  return !Array.isArray(value) || value.length === 0;
}

function isMissingRequired(
  schema: KaotoSchemaDefinition['schema'],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any,
  propertyName: string,
  propertySchema: KaotoSchemaDefinition['schema'],
): boolean {
  return (
    Array.isArray(schema.required) &&
    schema.required.includes(propertyName) &&
    propertySchema.default === undefined &&
    propertySchema.$ref === undefined &&
    (!model?.[propertyName] || (propertySchema.type === 'array' && isEmptyOrNotArray(model[propertyName])))
  );
}

/**
 * Service for validating the model of a node.
 * Ideally, this should be done with a JSON schema validator, like ajv, but for our use case, it's not possible,
 * since we need to validate the model against the schema, but also against the definition of the node.
 *
 * TODO: This service should be reshaped as a Manager entity which register many validators, and each validator
 * should be able to validate a specific aspect of the model. This way, we could have a validator for the schema,
 * and another one for the definition.
 *
 * For instance, the schema validator could be used to validate the model against the schema, and the definition
 * validator could be used to validate the model against the definition, and check if the model is complete. In addition to that,
 * we could validate the model against external constraints, like whether a property is defined in an environment variable or a
 * property file.
 */
export class ModelValidationService {
  static async validateNodeStatus(schema: KaotoSchemaDefinition['schema'], definition: unknown): Promise<string> {
    if (!schema) return '';
    let message = '';

    const validationResult = await this.validateRequiredProperties(schema, definition, '', schema.definitions);
    const missingProperties = validationResult
      .filter((result) => result.type === 'missingRequired')
      .map((result) => result.propertyName);

    if (missingProperties.length > 0) {
      message =
        missingProperties.length > 1
          ? `${missingProperties.length} required parameters are not yet configured: [ ${missingProperties} ]`
          : `1 required parameter is not yet configured: [ ${missingProperties} ]`;
    }

    return message;
  }

  private static async validateRequiredProperties(
    schema: KaotoSchemaDefinition['schema'],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    model: any,
    parentPath: string,
    definitions?: Record<string, KaotoSchemaDefinition['schema']>,
  ): Promise<IValidationResult[]> {
    const answer: IValidationResult[] = [];

    answer.push(
      ...(await this.validateAnyOf(schema, model, parentPath, definitions)),
      ...(await this.validateOneOf(schema, model, parentPath, definitions)),
      ...(await this.validateRef(schema, model, parentPath, definitions)),
      ...(await this.validateProperties(schema, model, parentPath, definitions)),
    );

    return answer;
  }

  private static async validateAnyOf(
    schema: KaotoSchemaDefinition['schema'],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    model: any,
    parentPath: string,
    definitions?: Record<string, KaotoSchemaDefinition['schema']>,
  ): Promise<IValidationResult[]> {
    if (!Array.isArray(schema.anyOf)) return [];

    const answer: IValidationResult[] = [];
    let parsedModel = model;
    const languageNames = await ExpressionService.getLanguageNames();
    for (const anyOfSchema of schema.anyOf) {
      if (isDefined(anyOfSchema.format) && anyOfSchema.format === 'expression') {
        parsedModel = ExpressionService.parseExpressionModel(languageNames, model);
      }
      answer.push(...(await this.validateRequiredProperties(anyOfSchema, parsedModel, parentPath, definitions)));
    }
    return answer;
  }

  private static async validateOneOf(
    schema: KaotoSchemaDefinition['schema'],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    model: any,
    parentPath: string,
    definitions?: Record<string, KaotoSchemaDefinition['schema']>,
  ): Promise<IValidationResult[]> {
    if (!Array.isArray(schema.oneOf)) return [];

    const answer: IValidationResult[] = [];
    for (const oneOfSchema of schema.oneOf) {
      answer.push(...(await this.validateRequiredProperties(oneOfSchema, model, parentPath, definitions)));
    }
    return answer;
  }

  private static async validateRef(
    schema: KaotoSchemaDefinition['schema'],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    model: any,
    parentPath: string,
    definitions?: Record<string, KaotoSchemaDefinition['schema']>,
  ): Promise<IValidationResult[]> {
    if (schema.properties || !schema.$ref) return [];

    const resolvedSchema = resolveSchemaWithRef(schema, definitions!);
    return this.validateRequiredProperties(resolvedSchema, model, parentPath, definitions);
  }

  private static async validateProperties(
    schema: KaotoSchemaDefinition['schema'],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    model: any,
    parentPath: string,
    definitions?: Record<string, KaotoSchemaDefinition['schema']>,
  ): Promise<IValidationResult[]> {
    if (!schema.properties) return [];

    const answer: IValidationResult[] = [];
    for (const [propertyName, propertyValue] of Object.entries(schema.properties)) {
      const propertySchema = propertyValue as KaotoSchemaDefinition['schema'];
      const path = parentPath ? `${parentPath}.${propertyName}` : propertyName;

      if (isMissingRequired(schema, model, propertyName, propertySchema)) {
        answer.push({
          level: 'error',
          type: 'missingRequired',
          parentPath,
          propertyName,
          message: `Missing required property ${propertyName}`,
        });
        continue;
      }

      if (model?.[propertyName] && propertySchema.$ref) {
        const resolvedPropertySchema = resolveSchemaWithRef(propertySchema, definitions!);
        answer.push(
          ...(await this.validateRequiredProperties(resolvedPropertySchema, model[propertyName], path, definitions)),
        );
      }

      if (propertySchema.type === 'object' && model) {
        answer.push(...(await this.validateRequiredProperties(propertySchema, model[propertyName], path, definitions)));
      }
    }
    return answer;
  }
}
